/**
 * Reine Zeitraster-Logik des Stundenplans – kein DOM, kein Backend.
 *
 * Grundidee: Der Nutzer pflegt nur Beginn und Ende jeder Unterrichtsstunde.
 * Alles andere (Pausen, Blocklängen, „wo stehen wir gerade") wird daraus
 * abgeleitet, damit es keine zweite Wahrheit gibt.
 */

import type { Period, TimetableSlot, Weekday } from "@/types/models";

/** Standard-Zeitraster: 10 Stunden mit den üblichen Pausen. */
export const DEFAULT_PERIODS: Period[] = [
  { start: "08:00", end: "08:45" },
  { start: "08:50", end: "09:35" },
  { start: "09:50", end: "10:35" },
  { start: "10:40", end: "11:25" },
  { start: "11:50", end: "12:35" },
  { start: "12:40", end: "13:25" },
  { start: "13:40", end: "14:25" },
  { start: "14:30", end: "15:15" },
  { start: "15:20", end: "16:05" },
  { start: "16:10", end: "16:55" },
];

export const DEFAULT_DAYS: Weekday[] = [0, 1, 2, 3, 4];

export const WEEKDAY_LABEL = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
export const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/** „HH:MM" -> Minuten seit Mitternacht. Ungültiges ergibt null. */
export function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Minuten seit Mitternacht -> „HH:MM". */
export function fromMinutes(total: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(total)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Mo=0 … So=6 (im Gegensatz zu Date.getDay(), das sonntags bei 0 startet). */
export function weekdayOf(date: Date): Weekday {
  return ((date.getDay() + 6) % 7) as Weekday;
}

/** Minuten seit Mitternacht für einen Zeitpunkt. */
export function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

/* ---------- Kalenderdaten (fürs Wochenraster mit echten Tagen) ---------- */

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Montag der Woche, die `date` enthält. */
export function mondayOf(date: Date): Date {
  return addDays(startOfDay(date), -weekdayOf(date));
}

/** Datum eines Wochentags innerhalb der Woche, die mit `monday` beginnt. */
export function dateForWeekday(monday: Date, weekday: Weekday): Date {
  return addDays(monday, weekday);
}

export function isSameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** ISO-8601-Kalenderwoche (Woche mit dem ersten Donnerstag des Jahres = KW 1). */
export function isoWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7; // Mo=0 … So=6
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // Donnerstag dieser Woche
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

/** „YYYY-MM-DD" in lokaler Zeit (kein UTC-Sprung wie bei toISOString()). */
export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Kurzes Datum, z. B. „16.8." – fürs Wochenraster (Tageskopf, Stundenauswahl). */
export function formatShortDate(date: Date): string {
  return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

/* ---------- Anstehende Stunden eines Fachs (für die Klausurtermin-Auswahl) ---------- */

/** Ein konkreter zukünftiger Termin eines belegten Blocks: Kalendertag + Stundenbereich. */
export interface UpcomingSlot {
  date: Date;
  weekday: Weekday;
  from: number;
  to: number;
}

/**
 * Nächste Kalendertage ab `from` (inklusive), an denen `weekday` vorkommt –
 * wöchentlich, also im 7-Tage-Abstand.
 */
export function upcomingDatesForWeekday(weekday: Weekday, from: Date, count: number): Date[] {
  const start = startOfDay(from);
  let diff = weekday - weekdayOf(start);
  if (diff < 0) diff += 7;
  const first = addDays(start, diff);
  const dates: Date[] = [];
  for (let i = 0; i < count; i++) dates.push(addDays(first, i * 7));
  return dates;
}

/**
 * Die nächsten anstehenden Stunden eines Fachs über alle angezeigten
 * Wochentage hinweg, chronologisch sortiert – Grundlage für die
 * Klausurtermin-Auswahl per Dropdown. Mehrstündige Blöcke (Doppelstunden)
 * erscheinen als ein Eintrag (siehe `buildBlocks`).
 */
export function upcomingSubjectSlots(opts: {
  slots: TimetableSlot[];
  periods: Period[];
  days: Weekday[];
  subjectId: string;
  from?: Date;
  weeksAhead?: number;
  /**
   * Heute schon begonnene Stunden auslassen (auch die gerade laufende).
   * Für Hausaufgaben: „bis zur nächsten Stunde" heißt nicht „bis zur Stunde,
   * in der ich gerade sitze".
   */
  skipStarted?: boolean;
}): UpcomingSlot[] {
  const from = opts.from ?? new Date();
  const weeksAhead = opts.weeksAhead ?? 8;
  const today = weekdayOf(from);
  const nowMinutes = minutesOfDay(from);
  const result: UpcomingSlot[] = [];
  for (const weekday of opts.days) {
    const blocks = buildBlocks(opts.slots, weekday, opts.periods.length).filter(
      (b) => b.subject === opts.subjectId
    );
    if (!blocks.length) continue;
    for (const date of upcomingDatesForWeekday(weekday, from, weeksAhead)) {
      for (const block of blocks) {
        const to = block.from + block.span - 1;
        // Heutige Stunde, aber schon vorbei: zaehlt nicht mehr als "naechste"
        // - sonst schlaegt der Vorschlag um 18 Uhr noch die laengst gelaufene
        // 8-Uhr-Stunde von heute vor (Symptom: Hausaufgaben-Frist immer "heute").
        if (weekday === today && isSameDate(date, from)) {
          const endMinutes = toMinutes(opts.periods[to - 1]?.end ?? "");
          if (endMinutes !== null && nowMinutes >= endMinutes) continue;
          const startMinutes = toMinutes(opts.periods[block.from - 1]?.start ?? "");
          if (opts.skipStarted && startMinutes !== null && nowMinutes >= startMinutes) continue;
        }
        result.push({ date, weekday, from: block.from, to });
      }
    }
  }
  return result.sort((a, b) => a.date.getTime() - b.date.getTime() || a.from - b.from);
}

/** Unterrichtsstunden eines Fachs von jetzt bis vor den Termin (am Termintag nur Stunden vor `untilPeriod`). */
export function lessonsUntil(opts: {
  slots: TimetableSlot[];
  periods: Period[];
  days: Weekday[];
  subjectId: string;
  until: string;
  untilPeriod?: number;
  isFree: (isoDate: string) => boolean;
}): number {
  const now = new Date();
  const end = new Date(`${opts.until}T00:00:00`);
  const weeks = Math.ceil((end.getTime() - now.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  if (weeks <= 0) return 0;
  return upcomingSubjectSlots({ ...opts, from: now, weeksAhead: weeks })
    .filter((s) => {
      const iso = toLocalISODate(s.date);
      if (iso > opts.until || opts.isFree(iso)) return false;
      return iso < opts.until || (opts.untilPeriod !== undefined && s.to < opts.untilPeriod);
    })
    .reduce((sum, s) => sum + (s.to - s.from + 1), 0);
}

/**
 * Verwirft unbrauchbare Zeilen und sortiert nach Beginn. Eine Stunde muss
 * ein gültiges Start-/Endpaar haben und darf nicht rückwärts laufen.
 */
export function normalizePeriods(periods: Period[]): Period[] {
  return periods
    .map((p) => ({ start: p.start.trim(), end: p.end.trim() }))
    .filter((p) => {
      const s = toMinutes(p.start);
      const e = toMinutes(p.end);
      return s !== null && e !== null && e > s;
    })
    .sort((a, b) => (toMinutes(a.start) ?? 0) - (toMinutes(b.start) ?? 0));
}

/**
 * Prüft das Raster auf Überschneidungen. Liefert die 1-basierten Nummern der
 * Stunden, die in die vorherige hineinragen – für eine Warnung im Dialog.
 */
export function overlappingPeriods(periods: Period[]): number[] {
  const bad: number[] = [];
  for (let i = 1; i < periods.length; i++) {
    const prevEnd = toMinutes(periods[i - 1].end);
    const start = toMinutes(periods[i].start);
    if (prevEnd !== null && start !== null && start < prevEnd) bad.push(i + 1);
  }
  return bad;
}

/** Pause zwischen Stunde i und i+1 in Minuten (0 = nahtlos). */
export function breakAfter(periods: Period[], index: number): number {
  const end = toMinutes(periods[index]?.end ?? "");
  const next = toMinutes(periods[index + 1]?.start ?? "");
  if (end === null || next === null) return 0;
  return Math.max(0, next - end);
}

export function periodDuration(period: Period): number {
  const s = toMinutes(period.start);
  const e = toMinutes(period.end);
  if (s === null || e === null) return 0;
  return Math.max(0, e - s);
}

/* ---------- Slots ---------- */

export type SlotMap = Map<string, TimetableSlot>;

export const slotKey = (weekday: number, period: number) => `${weekday}:${period}`;

export function indexSlots(slots: TimetableSlot[]): SlotMap {
  const map: SlotMap = new Map();
  for (const s of slots) map.set(slotKey(s.weekday, s.period), s);
  return map;
}

/**
 * Ein zusammenhängender Block gleicher Fächer an einem Tag.
 * `span` ist die Zahl der Stunden – dadurch wächst die Box im Raster über
 * die Pause hinweg, statt sie als Lücke zu zeigen.
 */
export interface SlotBlock {
  weekday: Weekday;
  /** 1-basierte Nummer der ersten Stunde des Blocks. */
  from: number;
  span: number;
  /** Leer bei einem Termin ohne Fach – dann sind die `event*`-Felder gesetzt. */
  subject?: string;
  eventTitle?: string;
  eventColor?: string;
  eventIcon?: string;
  room?: string;
  slots: TimetableSlot[];
}

/**
 * Zwei Felder gehören zum selben Block, wenn sie dasselbe Fach ODER
 * (ohne Fach) denselben Termin-Titel tragen – sonst würden zwei
 * verschiedene fachlose Termine an aufeinanderfolgenden Stunden (z. B. Chor
 * direkt gefolgt von einem anderen Termin) fälschlich zu einem Block
 * verschmelzen, nur weil beide kein `subject` haben.
 */
function slotIdentity(slot: TimetableSlot): string {
  return slot.subject || `event:${slot.eventTitle ?? ""}`;
}

/**
 * Fasst direkt aufeinanderfolgende Stunden desselben Fachs (oder desselben
 * fachlosen Termins) zu einem Block zusammen. Eine Lücke im Plan (freie
 * Stunde) trennt immer, eine Pause zwischen zwei belegten Stunden nicht.
 */
export function buildBlocks(slots: TimetableSlot[], weekday: Weekday, periodCount: number): SlotBlock[] {
  const byPeriod = new Map<number, TimetableSlot>();
  for (const s of slots) {
    if (s.weekday === weekday) byPeriod.set(s.period, s);
  }

  const blocks: SlotBlock[] = [];
  let current: SlotBlock | null = null;

  for (let p = 1; p <= periodCount; p++) {
    const slot = byPeriod.get(p);
    if (!slot) {
      current = null;
      continue;
    }
    const sameAsPrev =
      current && slotIdentity(current.slots[0]) === slotIdentity(slot) && current.from + current.span === p;
    if (sameAsPrev && current) {
      current.span += 1;
      current.slots.push(slot);
      // Raumangabe des ersten Slots gewinnt; ein späterer Raum füllt nur Lücken.
      if (!current.room && slot.room) current.room = slot.room;
    } else {
      current = {
        weekday,
        from: p,
        span: 1,
        subject: slot.subject,
        eventTitle: slot.eventTitle,
        eventColor: slot.eventColor,
        eventIcon: slot.eventIcon,
        room: slot.room,
        slots: [slot],
      };
      blocks.push(current);
    }
  }
  return blocks;
}

/* ---------- „Wo sind wir gerade?" ---------- */

export interface NowPosition {
  /** 1-basierte Nummer der laufenden Stunde, sonst null (Pause/außerhalb). */
  period: number | null;
  /** Anteil 0..1 innerhalb der laufenden Stunde. */
  progress: number;
  /** Minuten seit Mitternacht – Grundlage für die Zeitlinie. */
  minutes: number;
  weekday: Weekday;
}

export function nowPosition(periods: Period[], now = new Date()): NowPosition {
  const minutes = minutesOfDay(now);
  const pos: NowPosition = { period: null, progress: 0, minutes, weekday: weekdayOf(now) };
  for (let i = 0; i < periods.length; i++) {
    const s = toMinutes(periods[i].start);
    const e = toMinutes(periods[i].end);
    if (s === null || e === null) continue;
    if (minutes >= s && minutes < e) {
      pos.period = i + 1;
      pos.progress = (minutes - s) / Math.max(1, e - s);
      break;
    }
  }
  return pos;
}

/**
 * So viele Minuten nach Beginn einer neuen Stunde gilt noch die vorherige als
 * Bezug für „Hausaufgabe anlegen": die Hausaufgabe zur Stunde davor wird oft
 * erst nach dem Klingeln aufgegeben oder notiert.
 */
export const SWITCH_GRACE_MIN = 5;

/**
 * Stunde (1-basiert), auf die sich das Anlegen einer Hausaufgabe gerade
 * bezieht: die laufende, in der Pause die kommende, nach Schulschluss die
 * letzte. Aber erst `SWITCH_GRACE_MIN` Minuten nach Beginn einer neuen Stunde
 * wird auf sie umgeschaltet – vorher (auch in der Pause davor) bleibt es die
 * unmittelbar vorangehende Stunde, sofern es eine gab. Die erste Stunde des
 * Tages hat keine, dort gilt sie sofort.
 */
export function homeworkReferencePeriod(blocks: SlotBlock[], periods: Period[], now = new Date()): number {
  const pos = nowPosition(periods, now);
  let ref = pos.period ?? 0;
  if (!ref) {
    // Gerade Pause oder außerhalb des Unterrichts: an der nächsten Stunde
    // ausrichten (nach Schulschluss an der letzten).
    ref = periods.length;
    for (let i = 0; i < periods.length; i++) {
      const start = toMinutes(periods[i].start);
      if (start !== null && pos.minutes <= start) {
        ref = i + 1;
        break;
      }
    }
  }
  const start = toMinutes(periods[ref - 1]?.start ?? "");
  if (start !== null && pos.minutes < start + SWITCH_GRACE_MIN && blocks.some((b) => b.from + b.span - 1 === ref - 1)) {
    return ref - 1;
  }
  return ref;
}

/**
 * Die aktuell laufende Unterrichtsstunde am gegebenen Tag – oder null, wenn
 * gerade Pause, Freistunde oder Feierabend ist.
 */
export function currentSlot(
  slots: TimetableSlot[],
  periods: Period[],
  now = new Date()
): { slot: TimetableSlot; period: number } | null {
  const pos = nowPosition(periods, now);
  if (pos.period === null) return null;
  const slot = slots.find((s) => s.weekday === pos.weekday && s.period === pos.period);
  return slot ? { slot, period: pos.period } : null;
}
